import React, { useState, useEffect } from 'react';
import { receptionReportAPI } from '../services/api';
import PurchaseOrderDetailModal from './PurchaseOrderDetailModal';
import './Modal.css';

function IRDetailModal({ ir, onClose }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedPOId, setSelectedPOId] = useState(null);

    useEffect(() => {
        if (ir?.irHId) {
            loadDetails();
        }
    }, [ir]);

    const loadDetails = async () => {
        try {
            setLoading(true);
            const response = await receptionReportAPI.getDetail(ir.irHId);
            if (response.success) {
                setItems(response.data);
            } else {
                setError(response.error);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(val);
    };

    if (!ir) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ width: '80%', maxWidth: 'none' }}>
                <div className="modal-header">
                    <h2>Detalle de Informe #{ir.irHId}</h2>
                    <button className="close" onClick={onClose}>&times;</button>
                </div>

                <div className="modal-body">
                    <div className="info-row" style={{ marginBottom: '20px', padding: '10px', background: '#f8f9fa', borderRadius: '4px', display: 'flex', justifyContent: 'space-between' }}>
                        <div>
                            <div className="info-item"><strong>Fecha:</strong> {ir.irHDate}</div>
                            <div className="info-item"><strong>Usuario:</strong> {ir.irHUser}</div>
                            <div className="info-item"><strong>Comentario:</strong> {ir.irComment}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <strong>OCs Relacionadas:</strong><br />
                            {[...new Set(items.map(i => i.oc_id).filter(id => id))].map(ocId => (
                                <button
                                    key={ocId}
                                    className="btn btn-sm btn-info"
                                    style={{ marginLeft: '5px', marginTop: '5px' }}
                                    onClick={() => setSelectedPOId(ocId)}
                                >
                                    Ver OC #{ocId}
                                </button>
                            ))}
                        </div>
                    </div>

                    {loading ? (
                        <div className="loading">Cargando detalles...</div>
                    ) : error ? (
                        <div className="alert alert-error">{error}</div>
                    ) : items.length === 0 ? (
                        <div className="alert alert-info">No hay items en este informe.</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="comparison-table">
                                <thead>
                                    <tr>
                                        <th>Artículo</th>
                                        <th>Unidad</th>
                                        <th className="text-center">Prop.</th>
                                        <th className="text-center">Ref. OC</th>
                                        <th className="text-right">Cant. Pedida</th>
                                        <th className="text-right">Cant. Rec. (IR)</th>
                                        <th className="text-center">Estado</th>
                                        <th className="text-right">Precio OC</th>
                                        <th className="text-right">Total Valorizado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item, idx) => {
                                        // Calcular cantidades reales si hay factor de conversión (Presentación)
                                        const conversionFactor = item.presentation_qty || 1;

                                        const orderedQtyDisplay = conversionFactor > 1
                                            ? `${item.oc_quantity} (x${conversionFactor}) = ${(item.oc_quantity * conversionFactor)}`
                                            : item.oc_quantity;

                                        const totalOrderedUnits = item.oc_quantity * conversionFactor;

                                        // Use the greater of total_received from DB or current IR received to handle cases where DB isn't updated
                                        const effectiveReceived = Math.max(
                                            parseFloat(item.total_received_so_far || 0),
                                            parseFloat(item.irDReceivedQuantity || 0)
                                        );

                                        // Use epsilon for float comparison logic
                                        const isFulfilled = effectiveReceived >= (totalOrderedUnits - 0.01);

                                        const statusLabel = isFulfilled ? 'Cumplido' : 'Parcial';
                                        const statusClass = isFulfilled ? 'badge-success' : 'badge-warning';

                                        return (
                                            <tr key={item.irDId || idx}>
                                                <td>
                                                    <strong>{item.article_code}</strong><br />
                                                    <small>{item.article_description}</small>
                                                </td>
                                                <td>{item.unit}</td>
                                                <td className="text-center">
                                                    {conversionFactor > 1 ? conversionFactor : '-'}
                                                </td>
                                                <td className="text-center">
                                                    <span className="badge badge-info" style={{ background: '#17a2b8', color: 'white', fontSize: '0.85em' }}>
                                                        #{item.oc_id} / Ln {item.oc_line}
                                                    </span>
                                                </td>
                                                <td className="text-right">{orderedQtyDisplay}</td>
                                                <td className="text-right">{item.irDReceivedQuantity}</td>
                                                <td className="text-center">
                                                    <span className={`badge ${statusClass}`} style={{ fontSize: '0.8em' }}>
                                                        {statusLabel}
                                                    </span>
                                                </td>
                                                <td className="text-right">{formatCurrency(item.oc_price || 0)}</td>
                                                <td className="text-right">{formatCurrency(item.total_valued || 0)}</td>
                                            </tr>
                                        );
                                    })}
                                    <tr className="table-footer">
                                        <td colSpan="8" className="text-right"><strong>Total:</strong></td>
                                        <td className="text-right">
                                            <strong>{formatCurrency(items.reduce((acc, curr) => acc + (curr.total_valued || 0), 0))}</strong>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="modal-actions">
                    <button type="button" className="btn btn-secondary" onClick={onClose}>
                        Cerrar
                    </button>
                </div>
            </div>

            {selectedPOId && (
                <PurchaseOrderDetailModal
                    poId={selectedPOId}
                    onClose={() => setSelectedPOId(null)}
                />
            )}
        </div>
    );
}

export default IRDetailModal;
